import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { Building2, Plus, Search, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { platformApi } from '../api'
import { TenantStatusBadge } from '../components/TenantStatusBadge'
import type { TenantStatus } from '../types'

const FILTERS: Array<{ value: TenantStatus | 'ALL'; label: string }> = [
  { value: 'ALL', label: 'All' },
  { value: 'TRIAL', label: 'Trial' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PAST_DUE', label: 'Past due' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'CANCELLED', label: 'Cancelled' },
]

/** Every workspace on this installation, with what each one is using. */
export function PlatformTenantsPage() {
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<TenantStatus | 'ALL'>('ALL')

  const tenants = useQuery({ queryKey: ['platform', 'tenants'], queryFn: () => platformApi.tenants() })
  const estate = useQuery({ queryKey: ['platform', 'estate'], queryFn: () => platformApi.estate() })

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (tenants.data ?? []).filter((tenant) => {
      const matchesStatus = status === 'ALL' || tenant.status === status
      const matchesTerm =
        term === '' ||
        tenant.name.toLowerCase().includes(term) ||
        tenant.slug.toLowerCase().includes(term)
      return matchesStatus && matchesTerm
    })
  }, [tenants.data, search, status])

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        <div>
          <h1 className="text-xl font-semibold text-[var(--color-heading)]">Workspaces</h1>
          <p className="text-sm text-[var(--color-muted)]">
            Every customer on this installation.
          </p>
        </div>
        <Button asChild className="ms-auto">
          <Link to="/platform/tenants/new">
            <Plus className="me-2 size-4" aria-hidden />
            New workspace
          </Link>
        </Button>
      </div>

      {estate.data && (
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Tile label="Workspaces" value={estate.data.tenants} icon={Building2} />
          <Tile label="Active" value={estate.data.active} caption={`${estate.data.trial} on trial`} icon={Building2} />
          <Tile label="Employees" value={estate.data.totalEmployees} caption="across the estate" icon={Users} />
          <Tile
            label="Suspended"
            value={estate.data.suspended}
            caption={`${estate.data.pastDue} past due`}
            icon={Building2}
          />
        </dl>
      )}
      {estate.data && (
        <p className="text-sm text-[var(--color-muted)]">
          Default email sender:{' '}
          {estate.data.platformMailConfigured
            ? 'configured. Workspaces without their own mail server send through it.'
            : 'not configured (SMTP_HOST is not set). Workspaces without their own mail server send no email.'}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search
            className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-[var(--color-muted)]"
            aria-hidden
          />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by name or slug"
            aria-label="Search workspaces"
            className="w-64 ps-9"
          />
        </div>
        {FILTERS.map((filter) => (
          <Button
            key={filter.value}
            size="sm"
            variant={status === filter.value ? 'secondary' : 'ghost'}
            onClick={() => setStatus(filter.value)}
          >
            {filter.label}
          </Button>
        ))}
      </div>

      <div className="overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)]">
        <table className="w-full text-sm">
          <thead className="bg-[var(--color-page)] text-xs uppercase text-[var(--color-muted)]">
            <tr>
              <th className="px-4 py-3 text-start font-medium">Workspace</th>
              <th className="px-4 py-3 text-start font-medium">Status</th>
              <th className="px-4 py-3 text-start font-medium">Plan</th>
              <th className="px-4 py-3 text-end font-medium">Employees</th>
              <th className="px-4 py-3 text-end font-medium">Users</th>
              <th className="px-4 py-3 text-start font-medium">Metered</th>
            </tr>
          </thead>
          <tbody>
            {tenants.isPending && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-[var(--color-muted)]">
                  Loading…
                </td>
              </tr>
            )}

            {!tenants.isPending && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-[var(--color-muted)]">
                  No workspace matches that.
                </td>
              </tr>
            )}

            {rows.map((tenant) => (
              <tr key={tenant.id} className="border-t border-[var(--color-border)]">
                <td className="px-4 py-3">
                  <Link
                    to={`/platform/tenants/${tenant.id}`}
                    className="font-medium text-[var(--color-primary)] hover:underline"
                  >
                    {tenant.name}
                  </Link>
                  <div className="text-xs text-[var(--color-muted)]">{tenant.slug}</div>
                </td>
                <td className="px-4 py-3">
                  <TenantStatusBadge status={tenant.status} />
                  {tenant.status === 'TRIAL' && tenant.trialEndsOn && (
                    <div className="mt-1 text-xs text-[var(--color-muted)]">
                      until {tenant.trialEndsOn}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">{tenant.planCode ?? '—'}</td>
                <td className="px-4 py-3 text-end tabular-nums">
                  {tenant.activeEmployees ?? '—'}
                </td>
                <td className="px-4 py-3 text-end tabular-nums">{tenant.activeUsers ?? '—'}</td>
                <td className="px-4 py-3 text-xs text-[var(--color-muted)]">
                  {/* Blank until the nightly job has run once — better than showing a zero
                      that reads as "this customer has nobody". */}
                  {tenant.usageAsOf ?? 'not yet metered'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Tile({
  label,
  value,
  caption,
  icon: Icon,
}: {
  label: string
  value: number
  caption?: string
  icon: typeof Building2
}) {
  return (
    <div className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-4">
      <dt className="flex items-center gap-2 text-xs uppercase text-[var(--color-muted)]">
        <Icon className="size-4" aria-hidden />
        {label}
      </dt>
      <dd className="mt-2 text-2xl font-semibold tabular-nums text-[var(--color-heading)]">
        {value.toLocaleString()}
      </dd>
      {caption && <p className="text-xs text-[var(--color-muted)]">{caption}</p>}
    </div>
  )
}
